import { useEffect, useState, type ReactNode } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { useAuth } from '../contexts/AuthContext'
import { LanguageSwitcher } from './LanguageSwitcher'
import { setPendingAction } from '../lib/pendingAction'

export type PageKey =
  | 'dashboard'
  | 'leads'
  | 'clients'
  | 'orders'
  | 'projects'
  | 'calendar'
  | 'purchases'
  | 'finances'
  | 'tasks'
  | 'reports'
  | 'settings'

const navItems: Array<{ key: PageKey; icon: string }> = [
  { key: 'dashboard', icon: '⌂' },
  { key: 'leads', icon: '✦' },
  { key: 'clients', icon: '◎' },
  { key: 'orders', icon: '▣' },
  { key: 'projects', icon: '◇' },
  { key: 'calendar', icon: '□' },
  { key: 'purchases', icon: '↓' },
  { key: 'finances', icon: '₿' },
  { key: 'tasks', icon: '✓' },
  { key: 'reports', icon: '◫' },
  { key: 'settings', icon: '⚙' }
]


export function AppShell({
  page,
  onPageChange,
  children
}: {
  page: PageKey
  onPageChange: (page: PageKey) => void
  children: ReactNode
}) {
  const { t } = useLanguage()
  const { profile, user, signOut } = useAuth()
  const [quickOpen, setQuickOpen] = useState(false)

  useEffect(() => setQuickOpen(false), [page])

  const displayName = profile?.full_name || user?.email?.split('@')[0] || t('unknownUser')
  const quick = (targetPage: PageKey, action: string) => {
    setQuickOpen(false)
    setPendingAction(action)
    onPageChange(targetPage)
    window.setTimeout(() => window.dispatchEvent(new CustomEvent(`sgflow:${action}`)), 150)
  }

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">SG</div>
          <div><strong>{t('appName')}</strong><span>{t('appSubtitle')}</span></div>
        </div>
        <nav className="main-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={page === item.key ? 'active' : ''}
              onClick={() => onPageChange(item.key)}
              type="button"
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{t(item.key)}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <LanguageSwitcher />
          <div className="user-card">
            <div className="avatar">{displayName.slice(0, 1).toUpperCase()}</div>
            <div className="user-copy"><strong>{displayName}</strong><span>{profile?.role || ''}</span></div>
            <button className="icon-button" type="button" onClick={() => void signOut()} title={t('logout')}>↗</button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div><span className="eyebrow">{t('appSubtitle')}</span><h1>{t(page)}</h1></div>
          <div className="topbar-actions">
            <LanguageSwitcher />
            <button className="primary-button" onClick={() => setQuickOpen((value) => !value)} type="button">
              <span>＋</span> {t('quickAdd')}
            </button>
          </div>
          {quickOpen && (
            <div className="quick-menu">
              <button type="button" onClick={() => quick('leads', 'new-lead')}>{t('newLead')}</button>
              <button type="button" onClick={() => quick('clients', 'new-client')}>{t('newClient')}</button>
              <button type="button" onClick={() => quick('orders', 'new-order')}>{t('newOrder')}</button>
              <button type="button" onClick={() => quick('finances', 'new-expense')}>{t('newExpense')}</button>
            </div>
          )}
        </header>
        <div className="page-content">{children}</div>
      </main>

      <nav className="mobile-nav">
        {navItems.slice(0, 4).map((item) => (
          <button
            key={item.key}
            className={page === item.key ? 'active' : ''}
            onClick={() => onPageChange(item.key)}
            type="button"
          >
            <span>{item.icon}</span><small>{t(item.key)}</small>
          </button>
        ))}
        <button onClick={() => setQuickOpen((value) => !value)} type="button">
          <span>＋</span><small>{t('quickAdd')}</small>
        </button>
      </nav>
    </div>
  )
}
